import { convert } from './unitsManager';
const daysjs = require('../utils/dayjsConfig');

export const daysArray = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const minutesInDay = 60 * 24;
const halfAnHoursInWeek = 24 * 2 * 7;

const toMinutes = time => {
  const [hours, minutes] = time.split(':').map(val => parseInt(val, 10));
  return hours * 60 + minutes;
};

const getDayIndex = day => {
  let dayIndex = 0;
  switch (day) {
    case 'sunday':
      dayIndex = 0;
      break;
    case 'monday':
      dayIndex = 24 * 2;
      break;
    case 'tuesday':
      dayIndex = 24 * 2 * 2;
      break;
    case 'wednesday':
      dayIndex = 24 * 2 * 3;
      break;
    case 'thursday':
      dayIndex = 24 * 2 * 4;
      break;
    case 'friday':
      dayIndex = 24 * 2 * 5;
      break;
    case 'saturday':
      dayIndex = 24 * 2 * 6;
      break;
    default:
      break;
  }
  return dayIndex;
};

function getDayIndices(timeFrame) {
  let dayIndices = [];
  // If the day property of the current timeFrameKey includes the string "every"
  if (timeFrame.day.includes('every')) {
    // Map the daysArray and call getDayIndex on each element to get the day indices
    // we will go through all days of the week
    dayIndices = daysArray.map(day => getDayIndex(day));
  } else {
    dayIndices = [getDayIndex(timeFrame.day)];
  }
  return dayIndices;
}

const fillBoolArray = (timeFrames, timeFramesBoolArray, newTimeFrame) => {
  // Loop through the timeFrames object
  for (const timeFrameKey in timeFrames) {
    let dayIndices = getDayIndices(timeFrames[timeFrameKey]);

    for (let dayIndex of dayIndices) {
      const startTimeInMinutes = toMinutes(timeFrames[timeFrameKey].start_hour);
      const endTimeInMinutes = timeFrames[timeFrameKey].end_hour.includes('+1')
        ? toMinutes(timeFrames[timeFrameKey].end_hour.slice(0, -2)) +
          minutesInDay
        : toMinutes(timeFrames[timeFrameKey].end_hour);

      // Calculate the difference between the start and end times in half-hours
      const difference = (endTimeInMinutes - startTimeInMinutes) / 30;
      let startIndex = startTimeInMinutes / 30 + dayIndex;

      for (let i = startIndex; i < startIndex + difference; i++) {
        timeFramesBoolArray[i] = true;
      }

      // If the day index is 0, fill the same range of indices in the boolArray with true values, starting from the start time plus halfAnHoursInWeek ( for saturdays )
      if (dayIndex === 0) {
        for (
          let i = startTimeInMinutes + halfAnHoursInWeek;
          i < startTimeInMinutes + halfAnHoursInWeek + difference;
          i++
        ) {
          timeFramesBoolArray[i] = true;
        }
      }
    }
  }
};

// A time frame with no `active_period_end_date` is open-ended ("until further
// notice") and has no upper bound, so a null boundary compares as +infinity.
const isOnOrBeforeBoundary = (date, boundary) =>
  boundary === null || date.isSameOrBefore(boundary);

const haveOverlappingPeriods = (timeFrame1, timeFrame2) => {
  // A start date is still required to position a period at all. An end date is
  // not - previously a missing one short-circuited to `false`, which let an
  // open-ended frame silently escape overlap validation entirely.
  if (
    !timeFrame1.active_period_start_date ||
    !timeFrame2.active_period_start_date
  ) {
    return false;
  }

  const start1 = daysjs(timeFrame1.active_period_start_date);
  const start2 = daysjs(timeFrame2.active_period_start_date);
  const end1 = timeFrame1.active_period_end_date
    ? daysjs(timeFrame1.active_period_end_date)
    : null;
  const end2 = timeFrame2.active_period_end_date
    ? daysjs(timeFrame2.active_period_end_date)
    : null;

  // Check if the periods overlap
  const overlap =
    (start1.isSameOrBefore(start2) && isOnOrBeforeBoundary(start2, end1)) ||
    (start2.isSameOrBefore(start1) && isOnOrBeforeBoundary(start1, end2));

  return overlap;
};

export const validateChangedTimeFrame = (timeFrames, timeFrame) => {
  const errors = [];
  if (!timeFrame.day) errors.push({ field: 'day', message: 'Day is required' });
  if (timeFrame.min > timeFrame.max)
    errors.push({ field: 'min', message: 'min_smaller_than_max' });

  // An absent end date means open-ended, which is valid - only the start date
  // is required to run these checks. Gating the whole block on both dates being
  // present would skip overlap validation for open-ended frames.
  if (timeFrame.active_period_start_date) {
    if (
      timeFrame.active_period_end_date &&
      timeFrame.active_period_start_date > timeFrame.active_period_end_date
    ) {
      errors.push({
        field: 'startDate',
        message: 'Start date must be before end date.',
      });
    }
    for (let i = 0; i < timeFrames.length; i++) {
      let Overlap = haveOverlappingPeriods(timeFrame, timeFrames[i]);
      if (Overlap) {
        errors.push({
          field: 'startDate',
          message: 'Timeframe dates conflict with an existing timeframe.',
        });
      }
    }
  }

  // The purpose of the function is to validate the given time frame by checking if the time slot it defines is already taken or not
  const nextSixWeeks = 6 * 7 * 24 * 2; // number of half hours in 6 weeks
  const timeFramesBoolArray = Array(nextSixWeeks).fill(false);
  fillBoolArray(timeFrames, timeFramesBoolArray, timeFrame);

  let dayIndices = getDayIndices(timeFrame);
  for (let dayIndex of dayIndices) {
    let difference = 0;
    let startTimeMinutes = toMinutes(timeFrame.start_hour);
    if (timeFrame.end_hour.includes('+1')) {
      let endTimeMinutes =
        toMinutes(timeFrame.end_hour.slice(0, -2)) + minutesInDay;
      difference = ((endTimeMinutes - startTimeMinutes) / 60) * 2;
    } else {
      if (toMinutes(timeFrame.end_hour) - startTimeMinutes < 0) {
        errors.push({
          field: 'endHour',
          message: 'End hour must be after start hour.',
        });
      }
      difference =
        ((toMinutes(timeFrame.end_hour) - startTimeMinutes) / 60) * 2;
    }
    let start = startTimeMinutes / 30 + dayIndex;

    for (let i = start; i < start + difference; i++) {
      if (timeFramesBoolArray[i]) {
        errors.push({
          field: 'startHour',
          message: 'Timeframe slot is already taken.',
        });
      }
    }

    if (dayIndex === 0 || dayIndex === 288) {
      let startCircular = dayIndex === 0 ? start + halfAnHoursInWeek : start;
      for (let i = startCircular; i < startCircular + difference; i++) {
        if (timeFramesBoolArray[i]) {
          errors.push({
            field: 'startHour',
            message: 'Timeframe slot is already taken.',
          });
        }
      }
    }
  }
  return errors;
};

const validateOverlappingPeriods = (timeFrames, timeFrame, sowDate) => {
  const errors = [];
  // A missing start date still falls back to the sow date. A missing end date
  // is NOT filled with the season end - it means open-ended, and
  // `haveOverlappingPeriods` treats it as unbounded. Substituting endOfSeason
  // here would fail to flag a conflicting frame that starts after harvest.
  const checkedTimeFrame = {
    ...timeFrame,
    active_period_start_date: timeFrame.active_period_start_date || sowDate,
  };
  for (let i = 0; i < timeFrames.length; i++) {
    const comparedTimeFrame = {
      ...timeFrames[i],
      active_period_start_date:
        timeFrames[i].active_period_start_date || sowDate,
    };
    let Overlap = haveOverlappingPeriods(checkedTimeFrame, comparedTimeFrame);
    if (Overlap) {
      errors.push({
        field: 'startDate',
        overlapWith: timeFrames[i].id,
        message: 'Timeframe dates conflict with an existing timeframe.',
      });
    }
  }
  return errors;
};

export const validateByWeekTimeFrame = (
  timeFrames,
  timeFrame,
  sowDate,
  endOfSeason
) => {
  const errors = [];
  if (!timeFrame.frequency_x)
    errors.push({ field: 'frequency_x', message: 'Frequency is required' });
  if (timeFrame.min > timeFrame.max)
    errors.push({ field: 'min', message: 'min_smaller_than_max' });
  // Only comparable when an end date exists; without one the frame is
  // open-ended and can never start after its end.
  if (
    timeFrame.active_period_end_date &&
    Date.parse(timeFrame.active_period_start_date) >
      Date.parse(timeFrame.active_period_end_date)
  )
    errors.push({
      field: 'startDate',
      message: 'Start date must be before end date.',
    });

  let overlappingErrors = validateOverlappingPeriods(
    timeFrames,
    timeFrame,
    sowDate
  );

  return [...errors, ...overlappingErrors];
};

export const calculateMinMax = (
  timeFrame,
  fieldIrrigationSystemObj,
  fieldSoilType,
  soilData,
  userUnits,
  hasHours
) => {
  let minPlaceHolder = '';
  let maxPlaceHolder = '';
  let maxTooltipSource = '';
  let minSource = '';
  let maxSource = '';
  let irrigationType = fieldIrrigationSystemObj?.irrigation_type.toLowerCase();

  let soilDataObj = soilData?.soil_data[fieldSoilType?.toLowerCase()];
  // calculate min placeholder -based on soil type limitations
  if (irrigationType?.includes('sprinkler')) {
    maxPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['sprinkler'].max,
      'mm',
      userUnits,
      'length'
    );
    minPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['sprinkler'].min,
      'mm',
      userUnits,
      'length'
    );
  } else if (
    fieldIrrigationSystemObj?.irrigation_type.toLowerCase().includes('pivot')
  ) {
    maxPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['pivot'].max,
      'mm',
      userUnits,
      'length'
    );
    minPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['pivot'].min,
      'mm',
      userUnits,
      'length'
    );
  } else {
    maxPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['drip'].max,
      'mm',
      userUnits,
      'length'
    );
    minPlaceHolder = convert(
      soilDataObj?.irrigation_boundaries['drip'].min,
      'mm',
      userUnits,
      'length'
    );
  }

  if (
    timeFrame.hasOwnProperty('min') &&
    parseFloat(timeFrame.min) != minPlaceHolder
  ) {
    minSource = 'user';
  } else {
    minSource = 'soil';
  }
  let minTooltipSource = 'soil';
  maxSource = 'soil';
  maxTooltipSource = 'soil';

  if (Object.keys(timeFrame).length !== 0) {
    if (hasHours && timeFrame.start_hour && timeFrame.end_hour) {
      // calculate max placeholder -based on the min between soil type limitations and irrigation rate
      let timeFrameDuration = calculateHourDifference(
        timeFrame.start_hour,
        timeFrame.end_hour
      );
      let fieldIrrigationRate = fieldIrrigationSystemObj?.irrigation_rate;
      let irrigationRateValue = fieldIrrigationRate * timeFrameDuration;

      if (maxPlaceHolder < irrigationRateValue) {
        maxSource = 'soil';
      } else {
        maxPlaceHolder = irrigationRateValue;
        maxSource = 'rate';
        maxTooltipSource = 'rate';
      }
    }
    if (
      timeFrame.hasOwnProperty('max') &&
      parseFloat(timeFrame.max) != maxPlaceHolder
    ) {
      maxSource = 'user';
    }
  }
  return {
    minPlaceHolder,
    maxPlaceHolder,
    minSource,
    maxSource,
    minTooltipSource,
    maxTooltipSource,
  };
};

const calculateHourDifference = (startTime, endTime) => {
  const [startHour, startMinute] = startTime.split(':').map(Number);

  // Split endTime into hours, minutes, and offset
  const endTimeParts = endTime.split(/[+:]/);
  let endHour = parseInt(endTimeParts[0]);
  const endMinute = parseInt(endTimeParts[1]);
  const endDayOffset = endTime.includes('+')
    ? parseInt(endTimeParts[endTimeParts.length - 1])
    : 0;

  // If endTime is on the next day, add 24 hours
  endHour += endDayOffset * 24;

  // Convert times to hours
  const startTotalHours = startHour + startMinute / 60;
  const endTotalHours = endHour + endMinute / 60;

  // Calculate the difference
  let hourDifference = endTotalHours - startTotalHours;

  // If end time is before start time, assume it's on the next day
  if (hourDifference < 0) {
    hourDifference += 24;
  }
  return hourDifference;
};

const sortDays = (days = daysArray, startWithMonday = true) => {
  let sortedDays = [...days];
  if (startWithMonday) {
    sortedDays.push(sortedDays.shift());
  }
  return sortedDays;
};

export const getFirstDayOptions = (startWithMonday = true) => {
  return sortDays(daysArray, startWithMonday);
};

export const getDayOptions = (startWithMonday = true) => {
  let dayOptions = daysArray.map(day => ({
    title: day.charAt(0).toUpperCase() + day.slice(1),
    value: day,
  }));

  dayOptions = sortDays(dayOptions, startWithMonday);

  const recurringOptions = [
    { title: 'Every Day', value: 'everyDay' },
    {
      title: 'Every Two Days',
      value: 'every2Day',
    },
    { title: 'Every Three Days', value: 'every3Day' },
    {
      title: 'Every Four Days',
      value: 'every4Day',
    },
    { title: 'Every Five Days', value: 'every5Day' },
    {
      title: 'Every Six Days',
      value: 'every6Day',
    },
    { title: 'Every Seven Days', value: 'every7Day' },
    {
      title: 'Every Eight Days',
      value: 'every8Day',
    },
    { title: 'Every Nine Days', value: 'every9Day' },
    {
      title: 'Every Ten Days',
      value: 'every10Day',
    },
    { title: 'Every Two Days (Minimum)', value: 'minEvery2Day' },
    { title: 'Every Three Days (Minimum)', value: 'minEvery3Day' },
    { title: 'Every Four Days (Minimum)', value: 'minEvery4Day' },
    { title: 'Every Five Days (Minimum)', value: 'minEvery5Day' },
    { title: 'Every Six Days (Minimum)', value: 'minEvery6Day' },
    { title: 'Every Seven Days (Minimum)', value: 'minEvery7Day' },
    { title: 'Every Eight Days (Minimum)', value: 'minEvery8Day' },
    { title: 'Every Nine Days (Minimum)', value: 'minEvery9Day' },
    { title: 'Every Ten Days (Minimum)', value: 'minEvery10Day' },
  ];

  return [...dayOptions, ...recurringOptions];
};

export const getWeeklyOptions = () => {
  return [
    { title: 'No irrigation', value: 0 },
    { title: 'Once/week', value: 1 },
    { title: 'Max 2 times/week', value: 2 },
    { title: 'Max 3 times/week', value: 3 },
    { title: 'Max 4 times/week', value: 4 },
    { title: 'Max 5 times/week', value: 5 },
    { title: 'Max 6 times/week', value: 6 },
    { title: 'Max 7 times/week', value: 7 },
  ];
};

export const getIntervalOptions = () => {
  return [
    { title: 'No irrigation', value: 0 },
    { title: 'Every day', value: 1 },
    { title: 'Every 2 days or more', value: 2 },
    { title: 'Every 3 days or more', value: 3 },
    { title: 'Every 4 days or more', value: 4 },
    { title: 'Every 5 days or more', value: 5 },
    { title: 'Every 6 days or more', value: 6 },
    { title: 'Every 7 days', value: 7 },
  ];
};
